import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AppContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
};

export const AppProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [wishlist, setWishlist] = useState({ items: [] });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paypalConfig, setPaypalConfig] = useState(null);
  const sessionId = getSessionId();

  // Fetch products
  const fetchProducts = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.featured !== undefined) params.append('featured', filters.featured);
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch cart
  const fetchCart = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }, [sessionId]);

  // Fetch wishlist
  const fetchWishlist = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wishlist/${sessionId}`);
      setWishlist(response.data);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    }
  }, [sessionId]);

  // Fetch PayPal config
  const fetchPaypalConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/config/paypal`);
      setPaypalConfig(response.data);
    } catch (error) {
      console.error('Error fetching PayPal config:', error);
    }
  }, []);

  // Add to cart
  const addToCart = async (productId, quantity = 1, color = null) => {
    try {
      await axios.post(`${API}/cart/${sessionId}/add`, {
        product_id: productId,
        quantity,
        color
      });
      await fetchCart();
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return false;
    }
  };

  // Update cart item
  const updateCartItem = async (productId, quantity, color = null) => {
    try {
      await axios.put(`${API}/cart/${sessionId}/update`, {
        product_id: productId,
        quantity,
        color
      });
      await fetchCart();
      return true;
    } catch (error) {
      console.error('Error updating cart:', error);
      return false;
    }
  };

  // Remove from cart
  const removeFromCart = async (productId, color = null) => {
    try {
      const params = color ? `?color=${encodeURIComponent(color)}` : '';
      await axios.delete(`${API}/cart/${sessionId}/remove/${productId}${params}`);
      await fetchCart();
      return true;
    } catch (error) {
      console.error('Error removing from cart:', error);
      return false;
    }
  };

  // Clear cart
  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/${sessionId}/clear`);
      await fetchCart();
      return true;
    } catch (error) {
      console.error('Error clearing cart:', error);
      return false;
    }
  };

  // Add to wishlist
  const addToWishlist = async (productId) => {
    try {
      await axios.post(`${API}/wishlist/${sessionId}/add`, {
        product_id: productId
      });
      await fetchWishlist();
      return true;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return false;
    }
  };

  // Remove from wishlist
  const removeFromWishlist = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/${sessionId}/remove/${productId}`);
      await fetchWishlist();
      return true;
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return false;
    }
  };

  // Check if product is in wishlist
  const isInWishlist = (productId) => {
    return wishlist.items.some(item => item.id === productId);
  };

  // Create order
  const createOrder = async (shippingAddress) => {
    try {
      const response = await axios.post(`${API}/orders/create`, {
        session_id: sessionId,
        shipping_address: shippingAddress
      });
      return response.data;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  };

  // Capture order
  const captureOrder = async (orderId, paypalOrderId) => {
    try {
      const response = await axios.post(`${API}/orders/capture`, {
        order_id: orderId,
        paypal_order_id: paypalOrderId
      });
      await fetchCart();
      return response.data;
    } catch (error) {
      console.error('Error capturing order:', error);
      throw error;
    }
  };

  // Calculate cart total
  const cartTotal = cart.items.reduce((total, item) => {
    const price = item.product?.price || 0;
    return total + (price * item.quantity);
  }, 0);

  // Cart item count
  const cartItemCount = cart.items.reduce((count, item) => count + item.quantity, 0);

  // Initial data fetch
  useEffect(() => {
    fetchProducts();
    fetchCart();
    fetchWishlist();
    fetchPaypalConfig();
  }, [fetchProducts, fetchCart, fetchWishlist, fetchPaypalConfig]);

  const value = {
    // State
    cart,
    wishlist,
    products,
    loading,
    sessionId,
    paypalConfig,
    cartTotal,
    cartItemCount,
    
    // Actions
    fetchProducts,
    fetchCart,
    fetchWishlist,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    createOrder,
    captureOrder
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
